import { TStatus } from 'components/Status/Status';
import { SimpleAsset } from 'queries/assets/contract';
import { EventItem, FilterOptionsResponse } from 'queries/events/types';

export const caseTabs = {
  closed: 'CLOSED',
  open: 'OPEN',
  all: 'ALL',
} as const;

export type ActiveTab = 'ALL' | 'OPEN' | 'CLOSED';

export const caseStatus = {
  diagnosis: 'diagnosis',
  actions: 'actions',
  feedback: 'feedback',
  closed: 'closed',
  canceled: 'canceled',
} as const;
export const caseOpenStatus = [caseStatus.diagnosis, caseStatus.actions, caseStatus.feedback];

export const CaseStatusEnum = {
  diagnosis: 1,
  actions: 2,
  feedback: 3,
  closed: 4,
  canceled: 0,
};

export const caseStatusOrder = {
  ...CaseStatusEnum,
  canceled: 5,
};

export const conclusionStatus = {
  no_issue_found: 'no_issue_found',
  solved_completely: 'solved_completely',
  solved_partially: 'solved_partially',
  not_solved: 'not_solved',
} as const;

export const actionStatus = {
  on_track: 'on_track',
  delayed: 'delayed',
  executed: 'executed',
  none: 'none',
} as const;

export const ActionTypes = {
  maintenance: 'maintenance',
  operations: 'operations',
} as const;

export const detectionMethodOptions = {
  lighthouse: 'LIGHTHOUSE',
  PI_PROCESSBOOK: 'PI_PROCESSBOOK',
  PI_VISION: 'PI_VISION',
  SYSTEM_1: 'SYSTEM_1',
  'RMD_-_ICENTER': 'RMD_-_ICENTER',
  OFFLINE_VIBRATION_MONITORING: 'OFFLINE_VIBRATION_MONITORING',
  OFFLINE_OIL_MONITORING: 'OFFLINE_OIL_MONITORING',
  OTHERS: 'OTHERS',
  PDM_TOOL: 'PDM_TOOL',
  FIELD_INSPECTIO: 'FIELD_INSPECTION',
} as const;

export const disciplineOptions = {
  rotative: 'rotative',
  electrical: 'electrical',
  instrumentation: 'instrumentation',
  process: 'process',
  automation: 'automation',
  others: 'others',
} as const;

type TypeValues<T> = T[keyof T];
export type CaseStatus = TypeValues<typeof caseStatus>;
export type ActionStatus = TypeValues<typeof actionStatus>;
export type ConclusionStatus = TypeValues<typeof conclusionStatus>;
export type DetectionMethod = TypeValues<typeof detectionMethodOptions>;
export type DisciplineMethod = TypeValues<typeof disciplineOptions>;

export interface ICaseStep {
  status_options: CaseStatus[];
}

export const severityOptions = {
  very_high: 'very_high',
  high: 'high',
  medium: 'medium',
  low: 'low',
} as const;

export type Severity = TypeValues<typeof severityOptions>;

export const dimensionsOptions = {
  dynamic_condition: 'dynamic_condition',
  auxiliary_system: 'auxiliary_system',
  process_condition: 'process_condition',
  oil_analysis: 'oil_analysis',
} as const;

export type Dimensions = TypeValues<typeof dimensionsOptions>;

export interface ExternalReference {
  code?: string;
  type?: string;
  status?: string;
}

export type ExternalReferenceSummary = {
  [key: string]: number;
};

export type CreateAction = {
  creation_date: string;
  deleted: boolean;
  details?: string;
  due_date: string;
  end_date?: string | null;
  execution_feedback?: string;
  id?: string;
  item_id?: string;
  maintenance_notification?: string;
  external_reference?: ExternalReference | null;
  name: string;
  status: string;
  type_case_action: string;
};

export type Action = {
  creation_date: string | null;
  deleted: boolean;
  details?: string;
  due_date: string | null;
  end_date: string | null;
  execution_feedback?: string;
  external_reference?: ExternalReference | null;
  id?: string;
  item_id?: string;
  maintenance_notification?: string;
  name: string;
  status: ActionStatus;
  type_case_action: string;
};

export type ActionsArray = { actions: Action[] };

interface OptionalFields {
  cancel_reason?: string;
}

export type CaseBaseForm = {
  asset: SimpleAsset | null;
  id?: string;
  is_able_to_operate: boolean;
  name: string | null;
  owner_mail: string;
  start_date: string;
  status?: CaseStatus;
  updated_at: string | null;
  cancel_reason?: string | null;
};

export type DiagnosisForm = {
  anomaly_start: string | null;
  diagnosis: string;
  dimension: Dimensions | string;
  severity: Severity | string;
  detection_method: DetectionMethod;
  discipline: DisciplineMethod | string;
};

export type FeedbackForm = {
  anomaly_end: string | null;
  feedback_comments: string;
  conclusion: ConclusionStatus | string;
  review_date?: string | null;
};

export interface FileEvidences {
  name: string;
  file?: File;
  isNew?: boolean;
  isDeleted?: boolean;
}

export type EvidencesForm = {
  evidences: FileEvidences[];
  evidencesComments?: string[];
};

export interface Savings {
  description?: string;
  equipment_class?: string;
  failure_mechanism?: string;
  failure_mode?: string;
  maintainable_item?: string;
  maintenance_scope?: string;
  subunit?: string;
  total_estimated?: number;
}

export type SavingsType = {
  savings: Savings;
  has_savings: boolean;
};

export interface AlarmsType {
  id: string;
  is_automatic: boolean;
  is_automatic_blocked?: boolean;
  name: string;
}

export type RelatedAlarmForm = {
  alarms: Array<EventItem & { id: string; is_automatic?: boolean }>;
};

export type CaseForm = CaseBaseForm &
  DiagnosisForm &
  ActionsArray &
  FeedbackForm &
  SavingsType &
  RelatedAlarmForm &
  EvidencesForm & {
    event_code: string | null;
    team_a_review?: string | null;
    team_b_review?: string | null;
  } & OptionalFields;

export type CaseBasePayload = {
  alarms: AlarmsType[];
  asset_id: string;
  is_able_to_operate: boolean;
  name: string;
  owner_mail: string;
  savings: Savings;
  status: CaseStatus;
};

export type CaseCreatePayload = CaseBasePayload & {
  anomaly_start: string;
  diagnosis: string | null;
  dimension: string;
  severity: string;
  detection_method: DetectionMethod;
  discipline: DisciplineMethod;
};

export type CaseUpdatePayload = {
  id: string;
};

export type UpdateActionsPayload = Partial<CaseBasePayload> & {
  new_actions?: Action[];
  update_actions?: Action[];
};

export type UpdateDiagnosisPayload = CaseBasePayload & {
  anomaly_start: string;
  diagnosis: string;
  dimension: Dimensions;
  severity: Severity;
  detection_method: DetectionMethod;
  discipline: DisciplineMethod;
};

export type UpdateFeedbackPayload = CaseBasePayload & {
  conclusion: ConclusionStatus;
  feedback_comments: string;
  anomaly_end: string | null;
  review_date?: string | null;
};

export type CasePayload = CaseBasePayload &
  UpdateDiagnosisPayload &
  ActionsArray &
  UpdateActionsPayload &
  UpdateFeedbackPayload &
  SavingsType & {
    id?: string;
  };

export interface CasePatchPayload {
  id: string;
  alarms?: AlarmsType[];
  asset_id?: string;
  is_able_to_operate?: boolean;
  name?: string;
  owner_mail?: string;
  savings?: Savings;
}

export type CaseListItem = {
  action_status: string;
  anomaly_end: string | null;
  anomaly_start: string;
  asset: SimpleAsset;
  category?: string;
  children?: CaseListItem[];
  description: string;
  diagnosis: string;
  dimension: string;
  discipline: string;
  event_code: string | null;
  event_id: string;
  external_reference_summary: ExternalReferenceSummary;
  id: string;
  owner_mail: string;
  name: string;
  severity: string;
  start_date: string;
  status: CaseStatus;
  updated_at?: string;
  actions: Action[];
  detection_method: DetectionMethod;
  alarms_30d?: number;
  alarms_30_days?: { quantity: number; has_new: boolean } | null;
  is_able_to_operate?: boolean;
};

export const actionStatusSeverityMap: { [key in keyof typeof actionStatus]: TStatus } = {
  on_track: 'neutral',
  delayed: 'warning',
  executed: 'success',
  none: 'neutral',
} as const;

export type CaseOptions = {
  conclusion: ConclusionStatus[];
  detection_method: (DetectionMethod | '')[];
  dimension: (Dimensions | '')[];
  discipline: (DisciplineMethod | '')[];
  labels: {
    conclusion: string;
    dimension: string;
    severity: string;
  };
  savings: {
    equipment_class: {
      [equipment: string]: {
        [category: string]: string[];
      };
    };
    failure_mechanism: string[];
    failure_mode: string[];
    maintenance_scope: string[];
  };
  severity: (Severity | '')[];
  status: {
    [key in CaseStatus]: CaseStatus[];
  };
};

export type CaseOptionsKeys = keyof CaseOptions;

export const caseOptions = {
  conclusion: 'conclusion',
  detection_method: 'detection_method',
  dimension: 'dimension',
  discipline: 'discipline',
  labels: 'labels',
  savings: 'savings',
  severity: 'severity',
  status: 'status',
} as const;

export type CasesFilterOptionsResponse = Pick<FilterOptionsResponse, 'assets' | 'categories'> & {
  owners: string[];
};

export type DiagnosisFormWithAsset = DiagnosisForm & {
  name: string;
  asset: SimpleAsset;
};
